import { parseISO } from 'date-fns';
import type { RecordFacetDefinitions } from '@/composables/records/useRecordsAggregation';
import { i18n } from '@/plugins/internationalization/i18n';
import { signedDeviationPercent, type MedicineActiveLot, type MedicineInventoryRow } from '@/types';

/**
 * A drug in one unit, as "drugId:unitId": a drug counted in two units is two ledger keys, so the unit is part of
 * the identity. The one key format for the drug facet, the bulk count's rows and errors, and every per-drug list.
 */
export function drugUnitValue(row: { drug_id: number; unit_id: number }): string {
    return `${row.drug_id}:${row.unit_id}`;
}

export function drugUnitLabel(row: { drug_name: string; unit_name: string }): string {
    return `${row.drug_name} (${row.unit_name})`;
}

/** The facets both inventory tables offer: which farm, and which drug in which unit. */
export function farmAndDrugFacets<
    TRow extends {
        farm_id: number;
        farm_name: string;
        drug_id: number;
        drug_name: string;
        unit_id: number;
        unit_name: string;
    }
>(): RecordFacetDefinitions<TRow> {
    return {
        farm: { value: (row) => row.farm_id, label: (row) => row.farm_name },
        drug: { value: drugUnitValue, label: drugUnitLabel },
    };
}

/** Case-insensitive match of the search text against the farm and drug names. A blank search matches everything. */
export function matchesSearch(row: { farm_name: string; drug_name: string }, search: string): boolean {
    const needle = search.trim().toLowerCase();

    return needle === '' || row.farm_name.toLowerCase().includes(needle) || row.drug_name.toLowerCase().includes(needle);
}

/** How much of the active lot is left, 0 to 1; a lot that was never sized has nothing to show. */
export function lotFraction(lot: Pick<MedicineActiveLot, 'quantity' | 'remaining'>): number {
    return lot.quantity > 0 ? Math.min(1, Math.max(0, lot.remaining / lot.quantity)) : 0;
}

/**
 * The lot bar's tooltip: where the active lot started and what it has left, "Køb 05.03.2025 · 320 af 1.000 ml".
 * Empty when there is no lot to describe.
 */
export function lotTitle(row: Pick<MedicineInventoryRow, 'active_lot' | 'unit_name'>): string {
    const lot = row.active_lot;

    if (!lot) {
        return '';
    }

    const source = i18n.global.t(`views.health.inventory.chips.lot.${lot.source}`, { date: i18n.global.d(parseISO(lot.date), 'short') });
    const left = i18n.global.t('views.health.inventory.chips.lot.title', { remaining: formatQuantity(lot.remaining), quantity: formatQuantity(lot.quantity), unit: row.unit_name });

    return `${source} · ${left}`;
}

export type CountChipKind = 'none' | 'match' | 'difference';

/** How the last-count cell reads: never counted, counted and agreed with the ledger, or counted with a difference. */
export function countChipKind(lastCount: MedicineInventoryRow['last_count']): CountChipKind {
    if (lastCount === null) {
        return 'none';
    }

    return lastCount.difference === 0 ? 'match' : 'difference';
}

/** The colour a signed figure's sign gives it: a deduction red, an addition green, zero no colour of its own. */
export function signTone(value: number): string {
    if (value < 0) {
        return 'text-red-600';
    }

    return value > 0 ? 'text-success-700' : '';
}

/** A signed figure with an explicit plus for increases, so a difference never reads as a balance. */
export function signedNumber(value: number, format: (value: number) => string): string {
    return value > 0 ? `+${format(value)}` : format(value);
}

/** The two ids a drug value of the form "drugId:unitId" was built from. */
export function parseDrugUnitValue(value: string): { drugId: number; unitId: number } {
    const [drugId, unitId] = value.split(':').map(Number);

    return { drugId, unitId };
}

/** Quantities are stored to three decimals, so every inventory figure shows up to three (750 stays 750) and columns reconcile exactly. */
export function formatQuantity(value: number): string {
    return i18n.global.n(value, { key: 'decimal', maximumFractionDigits: 3 });
}

/** Rounds to the three decimals quantities are stored with, so float noise (0.1 + 0.2) does not read as a figure of its own. */
export function roundQuantity(value: number): number {
    return Math.round(value * 1000) / 1000;
}

/** How far a count may be off before its percentage reads as a warning, then as an error. */
const PERCENT_WARNING_ABOVE = 5;
const PERCENT_ERROR_ABOVE = 7;

export type PercentTone = 'ok' | 'warning' | 'error';

/**
 * The size of a count's deviation as a share of what the ledger expected: |difference| / calculated * 100.
 * Null when there is no count; otherwise as deviationPercent.
 */
export function countPercent(lastCount: MedicineInventoryRow['last_count']): number | null {
    return lastCount === null ? null : deviationPercent(lastCount.difference, lastCount.calculated);
}

/** |difference| / calculated * 100: the size of signedDeviationPercent, with its rules for a match and for nothing expected. */
export function deviationPercent(difference: number, calculated: number): number | null {
    const signed = signedDeviationPercent(difference, calculated);

    return signed === null ? null : Math.abs(signed);
}

/** Green up to 5 %, yellow above 5 up to 7, red above 7; the same bands the report colours its deviation by. */
export function percentTone(percent: number): PercentTone {
    if (percent > PERCENT_ERROR_ABOVE) {
        return 'error';
    }

    return percent > PERCENT_WARNING_ABOVE ? 'warning' : 'ok';
}

/** Badge classes of a tone, on top of the badge's outline variant. */
export const PERCENT_TONE_CLASSES: Record<PercentTone, string> = {
    ok: 'border-transparent bg-success-100 text-success-800',
    warning: 'border-transparent bg-yellow-100 text-yellow-900',
    error: 'border-transparent bg-error-100 text-error-800',
};

/** Text-only colour of a tone, for a figure that sits in a table cell without a badge. */
export const PERCENT_TONE_TEXT_CLASSES: Record<PercentTone, string> = {
    ok: 'text-success-700',
    warning: 'text-yellow-700',
    error: 'text-red-600',
};

/** A percentage (46 for 46 %) with up to one decimal, through the locale's percent format. */
export function formatPercent(value: number): string {
    return i18n.global.n(value / 100, { key: 'percent', maximumFractionDigits: 1 });
}

/** The label of a Seneste status facet value. */
export function countChipLabel(kind: CountChipKind): string {
    return i18n.global.t(`views.health.inventory.chips.status.${kind}`);
}

/** A signed percentage (-3.4 for -3.4 %) with an explicit plus for increases. */
export function formatSignedPercent(value: number): string {
    return value > 0 ? `+${formatPercent(value)}` : formatPercent(value);
}
